import { TABLE_CONTROLS_ELEVATED_Z_INDEX } from '@/components/tiptap-editor/tiptap-node/table-node/lib/table-floating-controls-portal';
import type { Orientation } from '@/components/tiptap-editor/tiptap-node/table-node/lib/tiptap-table-utils';
import { offset, size, useFloating, useTransitionStyles, type Placement } from '@floating-ui/react';
import { useCallback } from 'react';

interface TableExtendRowColumnButtonPositionResult {
    isMounted: boolean;
    setFloating: (node: HTMLElement | null) => void;
    setReferencePosition: (rect: DOMRect) => void;
    style: React.CSSProperties;
}

interface TableExtendRowColumnButtonsPositioningResult {
    rowIsMounted: boolean;
    rowSetFloating: (node: HTMLElement | null) => void;
    rowStyle: React.CSSProperties;
    columnIsMounted: boolean;
    columnSetFloating: (node: HTMLElement | null) => void;
    columnStyle: React.CSSProperties;
    setTableReferencePosition: (rect: DOMRect) => void;
}

const ORIENTATION_CONFIG = {
    row: {
        placement: 'bottom' as Placement,
        sizeProperty: 'width',
    },
    column: {
        placement: 'right' as Placement,
        sizeProperty: 'height',
    },
} as const;

function useTableExtendRowColumnButtonPosition(
    orientation: Orientation,
    show: boolean,
    fixedStrategy: boolean,
): TableExtendRowColumnButtonPositionResult {
    const config = ORIENTATION_CONFIG[orientation];

    const { refs, update, context, floatingStyles } = useFloating({
        open: show,
        placement: config.placement,
        ...(fixedStrategy ? { strategy: 'fixed' as const } : {}),
        middleware: [
            offset(4),
            size({
                apply({ rects, elements }) {
                    const floating = elements.floating;
                    if (!floating) return;

                    const sizeValue = `${rects.reference[config.sizeProperty]}px`;
                    floating.style[config.sizeProperty] = sizeValue;
                },
            }),
        ],
    });

    const { isMounted, styles } = useTransitionStyles(context);

    const setReferencePosition = useCallback(
        (rect: DOMRect) => {
            refs.setReference({
                getBoundingClientRect: () => rect,
            });
            update();
        },
        [refs, update],
    );

    return {
        isMounted,
        setFloating: refs.setFloating,
        setReferencePosition,
        style: {
            display: 'flex',
            pointerEvents: 'auto',
            ...(fixedStrategy ? { zIndex: TABLE_CONTROLS_ELEVATED_Z_INDEX } : {}),
            ...styles,
            ...floatingStyles,
        },
    };
}

export function useTableExtendRowColumnButtonsPositioning(
    showAddOrRemoveColumnsButton: boolean,
    showAddOrRemoveRowsButton: boolean,
    fixedStrategy: boolean,
): TableExtendRowColumnButtonsPositioningResult {
    const row = useTableExtendRowColumnButtonPosition('row', showAddOrRemoveRowsButton, fixedStrategy);
    const column = useTableExtendRowColumnButtonPosition('column', showAddOrRemoveColumnsButton, fixedStrategy);
    const { setReferencePosition: setRowReferencePosition } = row;
    const { setReferencePosition: setColumnReferencePosition } = column;

    const setTableReferencePosition = useCallback(
        (rect: DOMRect) => {
            setRowReferencePosition(rect);
            setColumnReferencePosition(rect);
        },
        [setRowReferencePosition, setColumnReferencePosition],
    );

    return {
        rowIsMounted: row.isMounted,
        rowSetFloating: row.setFloating,
        rowStyle: row.style,
        columnIsMounted: column.isMounted,
        columnSetFloating: column.setFloating,
        columnStyle: column.style,
        setTableReferencePosition,
    };
}
